import React, { useEffect, useState } from 'react';
import { cn } from '../../../utils/cn';
import { apiClient } from '../../../services/api';
import { fmtDate, inr, inrCompact, proofTier } from '../constants';
import { InvoiceRecord, Thread } from '../types';
import { LabelChips, OutreachChip } from './Chip';
import { AuditFlagBanner, ForensicPanel, RemittanceTable } from './DossierSections';
import { ThreadReader } from './ThreadReader';

/**
 * Open an attachment served by the auth-gated proxy (GET /recon/document?key=…).
 * A direct <a href> can't carry the Bearer token on a new-tab navigation, so we
 * fetch the bytes through axios (token auto-attached) and open the blob.
 */
async function openAttachment(proxyUrl: string) {
	try {
		// proxyUrl is "/v1/api/recon/document?key=…"; axios baseURL already has /v1/api
		const rel = proxyUrl.replace(/^\/v1\/api/, '');
		const res = await apiClient.get(rel, { responseType: 'blob' });
		const blobUrl = URL.createObjectURL(res.data as Blob);
		window.open(blobUrl, '_blank', 'noopener,noreferrer');
		// revoke later so the new tab has time to load
		setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
	} catch {
		// swallow — the chip stays; nothing to open
	}
}

/**
 * Invoice dossier — slide-over drawer. Core-first scope: the Zoho-vs-email
 * comparison, the follow-up recommendation, and verbatim payment evidence.
 * (Remittance-reconciliation table, forensic-audit panel, and thread modal are
 * deferred to pass 2 — see CASHFLOW_COCKPIT_INTEGRATION notes.)
 */

const MATCH_BADGE: Record<string, { text: string; cls: string }> = {
	ai_confirmed: { text: '✓ AI-confirmed match', cls: 'bg-green-100 text-green-700' },
	ai_rejected: { text: '⚠ match AI-rejected', cls: 'bg-red-100 text-red-700' },
	ai_unconfirmed: { text: '⚠ match unconfirmed', cls: 'bg-amber-100 text-amber-700' },
};

interface Props {
	invoice: InvoiceRecord | null;
	onClose: () => void;
}

export const Dossier: React.FC<Props> = ({ invoice, onClose }) => {
	const [openThread, setOpenThread] = useState<Thread | null>(null);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (openThread ? setOpenThread(null) : onClose());
		if (invoice) document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [invoice, onClose, openThread]);

	if (!invoice) return null;
	const r = invoice;
	const z = r.z;
	const c = r.c;
	const fu = r.fu;
	const badge = MATCH_BADGE[r.match] || MATCH_BADGE.ai_unconfirmed;

	// payment claims: prefer the ones asserting "paid", else show the demands
	const paid = (r.claims || []).filter(x => x.paid);
	const demand = (r.claims || []).filter(x => !x.paid);
	const showClaims = paid.length ? paid : demand;

	return (
		<div className='fixed inset-0 z-50 flex justify-end bg-black/40' onClick={onClose}>
			<div
				className='flex h-full w-full max-w-3xl flex-col bg-slate-50 shadow-2xl'
				onClick={e => e.stopPropagation()}
			>
				{/* Header */}
				<div className='flex items-start justify-between border-b border-slate-200 bg-white px-6 py-5'>
					<div>
						<h2 className='font-mono text-xl font-bold tracking-tight text-slate-900'>{r.num}</h2>
						<div className='mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500'>
							<span className={cn('rounded px-1.5 py-0.5 text-[11px] font-semibold', badge.cls)}>
								{badge.text}
							</span>
							<OutreachChip state={r.ostate} />
							<span className='font-medium text-slate-600'>{r.cust || '—'}</span>
							{/* When the forensic audit corrects this invoice, drop the misleading
							    structured labels (leak / hard-proof) so the dossier tells ONE story. */}
							<LabelChips labels={r.labels} auditCorrected={!!r.audit_flag} />
						</div>
					</div>
					<button
						onClick={onClose}
						className='rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700'
						aria-label='Close'
					>
						✕
					</button>
				</div>

				{/* Body */}
				<div className='flex-1 space-y-5 overflow-y-auto p-6'>
					{/* Zoho vs Email */}
					<div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
						<div className='rounded-xl border border-slate-200 bg-white p-4 shadow-sm'>
							<h3 className='mb-3 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500'>
								◆ Zoho — system of record
							</h3>
							<KV label='Status'>
								<span className={z.status === 'paid' ? 'text-green-600' : 'text-red-600'}>
									{z.status || '—'}
								</span>
							</KV>
							<KV label='Balance outstanding'>
								<span className={z.bal === 0 ? 'text-green-600' : 'text-red-600'}>
									{z.bal === 0 ? '₹0 — fully paid' : inr(z.bal)}
								</span>
							</KV>
							<KV label='Invoice total'>{inr(z.total)}</KV>
							<KV label='Raised on'>{fmtDate(z.idate)}</KV>
							<KV label='Due'>
								{fmtDate(z.due)}
								{z.od ? <span className='text-red-600'> · {z.od}d overdue</span> : null}
							</KV>
							<KV label='Payment recorded in Zoho'>
								{z.paydate ? (
									<>
										{fmtDate(z.paydate)}
										{z.payamt ? ` · ${inrCompact(Number(z.payamt))}` : ''}
										{z.paymode ? ` · ${z.paymode}` : ''}
									</>
								) : z.status === 'paid' ? (
									<span className='text-gray-500'>paid — date not synced</span>
								) : (
									<span className='text-red-600'>not recorded</span>
								)}
							</KV>
						</div>

						<div className='rounded-xl border border-slate-200 bg-white p-4 shadow-sm'>
							<h3 className='mb-3 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500'>
								◆ Email — what the client says
							</h3>
							<KV label='Customer claims paid'>
								{r.audit_flag ? (
									<span className='inline-block max-w-[230px] text-right'>
										<span className='font-semibold text-amber-600'>⚠ unverified</span>
										<span className='mt-0.5 block text-[10.5px] font-normal leading-snug text-slate-500'>
											pattern-match saw “paid”/UTRs, but the full-thread audit found this{' '}
											{r.audit_flag.kind === 'misclassified'
												? 'isn’t about this receivable'
												: 'unverified — no payment proof for this invoice'}
										</span>
									</span>
								) : r.cpaid ? (
									<span className='font-semibold text-red-600'>YES</span>
								) : (
									'no'
								)}
							</KV>
							<KV label="Our team ack'd paid">
								{r.ibpaid ? <span className='font-semibold text-red-600'>YES</span> : 'no'}
							</KV>
							<KV label='Strongest proof'>
								{r.audit_flag ? (
									<span className='rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700'>
										not validated for this invoice
									</span>
								) : (
									<span
										className={cn(
											'rounded px-1.5 py-0.5 text-[11px] font-semibold',
											proofTier(r.tier).cls
										)}
									>
										{proofTier(r.tier).label}
									</span>
								)}
							</KV>
							<KV label='Sentiment'>{c?.sent || '—'}</KV>
							<KV label='Ball in court'>{c?.ball || '—'}</KV>

							{/* References render full-width as chips — long UTR numbers must not
							    be comma-jammed into the right-aligned KV cell. */}
							<div className='mt-3 border-t border-slate-50 pt-3'>
								<div className='mb-1.5 text-[11px] text-slate-400'>References (UTR / NEFT)</div>
								{r.refs && r.refs.length ? (
									<div className='flex flex-wrap gap-1.5'>
										{r.refs.map((ref, i) => (
											<span
												key={i}
												className='rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600'
											>
												{ref}
											</span>
										))}
									</div>
								) : (
									<span className='text-sm text-slate-400'>—</span>
								)}
								{r.audit_flag && r.refs && r.refs.length > 0 && (
									<div className='mt-1.5 text-[10.5px] leading-snug text-amber-600'>
										⚠ may belong to other invoices in a bulk remittance — not confirmed for this one
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Follow-up recommendation — suppressed when the forensic audit corrects this
					    invoice (the structured action assumes proof the audit rejected; the
					    AuditFlagBanner below states the real situation instead). */}
					{fu && !r.audit_flag && (
						<div
							className={cn(
								'flex gap-3 rounded-lg border p-4',
								fu.action === 'send'
									? 'border-amber-200 bg-amber-50'
									: fu.action === 'verify_reconcile'
										? 'border-blue-200 bg-blue-50'
										: 'border-green-200 bg-green-50'
							)}
						>
							<div className='text-lg'>
								{fu.action === 'send' ? '▲' : fu.action === 'verify_reconcile' ? '◆' : '●'}
							</div>
							<div className='flex-1'>
								<div className='font-semibold text-gray-900'>
									{fu.action === 'send'
										? 'Send a follow-up'
										: fu.action === 'verify_reconcile'
											? 'Verify & Reconcile'
											: 'Hold — do not chase'}
								</div>
								{fu.why && <div className='mt-0.5 text-sm text-gray-700'>{fu.why}</div>}
								{fu.next && (
									<div className='mt-1 text-xs text-gray-600'>
										Next: {fu.next}
										{fu.to ? (
											<>
												{' '}
												· to <b>{fu.to}</b>
											</>
										) : null}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Audit flag — where the Pro audit corrects the dashboard */}
					<AuditFlagBanner flag={r.audit_flag} />

					{/* Forensic audit (Pro) — tactics + adjudicated claims */}
					<ForensicPanel forensic={r.forensic} />

					{/* Remittance reconciliation — client's payment advice vs Zoho */}
					<RemittanceTable invoice={r} />

					{/* Payment evidence — verbatim */}
					<Section title='What was said about payment'>
						{showClaims.length ? (
							<div className='space-y-2'>
								{showClaims.map((cl, i) => (
									<div
										key={i}
										className={cn(
											'rounded-lg border p-3',
											cl.paid ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-white'
										)}
									>
										<div className='mb-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500'>
											<b className='text-gray-700'>{cl.by || '?'}</b>
											{cl.tier && (
												<span className='rounded bg-gray-100 px-1.5 py-0.5 text-[10px]'>
													{cl.tier.replace('_', ' ')}
												</span>
											)}
											{cl.date && <span>claimed {cl.date}</span>}
											{cl.link && (
												<a
													href={cl.link}
													target='_blank'
													rel='noopener noreferrer'
													className='text-blue-600 hover:underline'
												>
													open in Gmail ↗
												</a>
											)}
										</div>
										{cl.subject && (
											<div className='mb-1 text-xs font-medium text-gray-600'>✉ {cl.subject}</div>
										)}
										<div className='text-sm italic text-gray-800'>
											&quot;{cl.quote || cl.claim}&quot;
										</div>
									</div>
								))}
							</div>
						) : (
							<div className='text-sm text-gray-400'>
								Nothing quoted about payment — see the email conversations below.
							</div>
						)}
					</Section>

					{/* Conversation summary */}
					{c && (
						<Section title='Conversation summary'>
							<div className='grid grid-cols-3 gap-3 sm:grid-cols-4'>
								<Stat n={c.emails} l='emails' />
								<Stat n={c.threads} l='threads' />
								<Stat n={c.quiet != null ? `${c.quiet}d` : '—'} l='since last email' />
								<Stat
									n={(c.risks || []).filter(Boolean).join(', ') || 'none'}
									l='issues raised'
									small
								/>
							</div>
							{c.summary && (
								<div className='mt-3 rounded-xl border border-slate-100 bg-white p-3 text-sm leading-relaxed text-slate-600 shadow-sm'>
									{c.summary}
								</div>
							)}
						</Section>
					)}

					{/* Email conversations (click to read the timeline) */}
					{r.thr && r.thr.length > 0 && (
						<Section title='Email conversations'>
							<div className='space-y-1.5'>
								{r.thr.map((t, i) => (
									<div
										key={i}
										onClick={() => (t.tl && t.tl.length ? setOpenThread(t) : undefined)}
										className={cn(
											'flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2 text-sm',
											t.tl && t.tl.length ? 'cursor-pointer hover:bg-gray-50' : ''
										)}
									>
										<span className='text-xs text-gray-400'>{t.msgs}✉</span>
										<span className='flex-1 text-gray-700' title={t.sum}>
											{t.subject ? <b className='text-gray-900'>{t.subject}</b> : null}
											{t.subject ? ' — ' : ''}
											<span className='text-gray-500'>{t.sum || t.id}</span>
										</span>
										{t.tl && t.tl.length ? (
											<span className='text-xs text-blue-600'>read →</span>
										) : null}
										{t.link && (
											<a
												href={t.link}
												target='_blank'
												rel='noopener noreferrer'
												onClick={e => e.stopPropagation()}
												className='text-xs text-blue-600 hover:underline'
											>
												Gmail ↗
											</a>
										)}
									</div>
								))}
							</div>
						</Section>
					)}

					{/* Attachments — deduped (one row per unique file), proofs first */}
					{r.docs && r.docs.length > 0 && (
						<Section title='Attachments'>
							{(() => {
								const proofs = r.docs!.filter(d => d.isProof).length;
								const invoices = r.docs!.filter(d => d.isInvoice && !d.isProof).length;
								return (
									<div className='mb-2 flex flex-wrap gap-3 text-xs'>
										{proofs > 0 && (
											<span className='font-medium text-emerald-600'>{proofs} payment proof{proofs > 1 ? 's' : ''}</span>
										)}
										{invoices > 0 && (
											<span className='font-medium text-blue-600'>{invoices} invoice{invoices > 1 ? 's' : ''}</span>
										)}
										<span className='text-slate-400'>· {r.docs!.length} unique file{r.docs!.length > 1 ? 's' : ''}</span>
									</div>
								);
							})()}
							<div className='space-y-1'>
								{r.docs!.slice(0, 12).map((d, i) => (
									<div
										key={i}
										className='flex items-center gap-2 rounded-lg border border-slate-100 px-2.5 py-1.5 text-sm'
									>
										<span
											className={cn(
												'rounded px-1.5 py-0.5 text-[10px] font-semibold',
												d.isProof
													? 'bg-emerald-50 text-emerald-700'
													: d.isInvoice
														? 'bg-blue-50 text-blue-700'
														: 'bg-slate-100 text-slate-400'
											)}
										>
											{d.isProof ? 'proof' : d.isInvoice ? 'invoice' : d.k}
										</span>
										<span className='flex-1 truncate text-slate-600' title={d.f}>
											{d.f}
										</span>
										{d.url ? (
											<button
												type='button'
												onClick={() => openAttachment(d.url!)}
												className='text-xs text-blue-600 hover:underline'
											>
												open ↗
											</button>
										) : (
											<span className='text-[10px] text-slate-300'>no link yet</span>
										)}
									</div>
								))}
								{r.docs!.length > 12 && (
									<div className='pt-1 text-[11px] text-slate-400'>
										+ {r.docs!.length - 12} more
									</div>
								)}
							</div>
						</Section>
					)}
				</div>
			</div>

			{/* In-app thread reader (reconstructed conversation) */}
			<ThreadReader
				thread={openThread}
				invoiceNum={r.num}
				customer={r.cust}
				onClose={() => setOpenThread(null)}
			/>
		</div>
	);
};

const KV: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className='flex items-center justify-between border-b border-slate-50 py-1.5 text-sm last:border-0'>
		<span className='text-slate-400'>{label}</span>
		<span className='font-semibold tabular-nums text-slate-800'>{children}</span>
	</div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
	<div>
		<h4 className='mb-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400'>
			{title}
		</h4>
		{children}
	</div>
);

const Stat: React.FC<{ n: React.ReactNode; l: string; small?: boolean }> = ({ n, l, small }) => (
	<div className='rounded-xl border border-slate-100 bg-white p-2.5 text-center shadow-sm'>
		<div
			className={cn(
				'font-bold tabular-nums text-slate-900',
				small ? 'text-xs leading-tight' : 'text-lg'
			)}
		>
			{n}
		</div>
		<div className='text-[11px] text-slate-400'>{l}</div>
	</div>
);
